import { FeatureFlagsKeysEnum, type IIntegration, providers as novuProviders } from '@novu/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleDashed } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiArrowRightSLine } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  type AgentIntegrationLink,
  type AgentResponse,
  getAgentIntegrationsQueryKey,
  listAgentIntegrations,
} from '@/api/agents';
import type { PlanUsage } from '@/api/agents-plan-usage';
import { filterUserRolloutLinks } from '@/components/agents/agent-integration-guides/whats-next/whats-next-config';
import { ProviderIcon } from '@/components/integrations/components/provider-icon';
import { Button } from '@/components/primitives/button';
import TruncatedText from '@/components/truncated-text';
import { requireEnvironment, useEnvironment } from '@/context/environment/hooks';
import { useAgentChannelsRolloutStatus } from '@/hooks/use-agent-channels-rollout-status';
import { useAgentRoutes } from '@/hooks/use-agent-routes';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useWhatsNextGuideSession } from '@/hooks/use-whats-next-default-expanded';
import { getAgentChannelDisplayName } from '@/utils/agent-email-provider-display';
import { buildRoute } from '@/utils/routes';
import { cn } from '@/utils/ui';
import { shouldShowAgentWhatsNextSection } from '@/utils/whats-next-guide';
import { AddChannelPicker } from './add-channel-picker';
import { hasAgentInboundConnection } from './is-agent-integration-connected';
import { SetupGuideCard } from './setup-guide-card';
import { SetupStep, SetupStepperRail } from './setup-guide-primitives';

const FADE_HEIGHT_PX = 43;
const COLLAPSED_VISIBLE_CHANNEL_COUNT = 4;
// Collapsed height fits exactly 4 rows (matches the Figma "What's next" spec).
const COLLAPSED_LIST_MAX_HEIGHT_PX = 150;

type AgentWhatsNextSectionProps = {
  agent: AgentResponse;
};

function buildVerticalFadeMask(
  showTopFade: boolean,
  showBottomFade: boolean,
  listHeightPx: number
): string | undefined {
  if (!showTopFade && !showBottomFade) {
    return undefined;
  }

  const topStart = showTopFade ? 'transparent 0' : 'black 0';
  const topStop = showTopFade ? `black ${FADE_HEIGHT_PX}px` : '';
  const bottomStop = showBottomFade ? `black ${Math.max(0, listHeightPx - FADE_HEIGHT_PX)}px` : '';
  const bottomEnd = showBottomFade ? 'transparent 100%' : 'black 100%';

  const stops = [topStart, topStop, bottomStop, bottomEnd].filter(Boolean).join(', ');

  return `linear-gradient(to bottom, ${stops})`;
}

function ConfigureChannelButton({
  link,
  onConfigure,
}: {
  link: AgentIntegrationLink;
  onConfigure: (link: AgentIntegrationLink) => void;
}) {
  const providerMeta = novuProviders.find((p) => p.id === link.integration.providerId);
  const displayName = getAgentChannelDisplayName(
    link.integration.providerId,
    providerMeta?.displayName ?? link.integration.name
  );

  return (
    <button
      type="button"
      onClick={() => onConfigure(link)}
      className={cn(
        'flex w-full max-w-[275px] shrink-0 items-center gap-0.5 overflow-hidden rounded-md p-1.5',
        'bg-bg-white bg-[linear-gradient(180deg,rgba(0,0,0,0)_30%,rgba(0,0,0,0.02)_100%)]',
        'shadow-[0px_1px_3px_0px_rgba(14,18,27,0.12),0px_0px_0px_1px_#e1e4ea]',
        'transition-shadow hover:shadow-[0px_1px_3px_0px_rgba(14,18,27,0.16),0px_0px_0px_1px_#cdd0d8]'
      )}
    >
      <CircleDashed className="text-text-sub size-4 shrink-0" />
      <span className="text-text-sub text-label-xs flex min-w-0 flex-1 items-center gap-1 px-1 font-medium">
        <span className="shrink-0">Setup</span>
        <span className="bg-bg-weak border-stroke-soft/50 flex min-w-0 shrink items-center gap-1 rounded border px-1 py-0.5">
          <ProviderIcon
            providerId={link.integration.providerId}
            providerDisplayName={displayName}
            className="size-[15px] shrink-0"
          />
          <TruncatedText className="text-text-strong min-w-0">{displayName}</TruncatedText>
        </span>
        <span className="shrink-0">for your users</span>
      </span>
      <RiArrowRightSLine className="text-text-sub size-4 shrink-0" />
    </button>
  );
}

function ChannelList({
  links,
  onConfigure,
}: {
  links: AgentIntegrationLink[];
  onConfigure: (link: AgentIntegrationLink) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const isCollapsible = links.length > COLLAPSED_VISIBLE_CHANNEL_COUNT;
  // Collapsed: bounded + scrollable. Expanded: unbounded so the full list is revealed.
  const listMaxHeightPx = isExpanded ? undefined : COLLAPSED_LIST_MAX_HEIGHT_PX;

  const updateFades = useCallback(() => {
    const node = listRef.current;

    if (!node || !isCollapsible) {
      setShowTopFade(false);
      setShowBottomFade(false);

      return;
    }

    const hasOverflow = node.scrollHeight > node.clientHeight + 1;
    const isScrolledToTop = node.scrollTop <= 1;
    const isScrolledToBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;

    setShowTopFade(hasOverflow && !isScrolledToTop);
    setShowBottomFade(hasOverflow && !isScrolledToBottom);
  }, [isCollapsible]);

  useEffect(() => {
    updateFades();
  }, [links.length, isExpanded, updateFades]);

  useEffect(() => {
    const node = listRef.current;

    if (!node) {
      return;
    }

    node.addEventListener('scroll', updateFades, { passive: true });

    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(node);

    return () => {
      node.removeEventListener('scroll', updateFades);
      resizeObserver.disconnect();
    };
  }, [updateFades]);

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="flex w-full max-w-[275px] flex-col gap-2.5">
      <div
        ref={listRef}
        className={cn(
          'flex w-full flex-col gap-2.5',
          // 1px padding keeps each row's box-shadow border ring from being clipped by the overflow container.
          isCollapsible && 'overflow-y-auto p-px'
        )}
        style={{
          maxHeight: isCollapsible ? listMaxHeightPx : undefined,
          maskImage: buildVerticalFadeMask(showTopFade, showBottomFade, COLLAPSED_LIST_MAX_HEIGHT_PX),
          WebkitMaskImage: buildVerticalFadeMask(showTopFade, showBottomFade, COLLAPSED_LIST_MAX_HEIGHT_PX),
        }}
      >
        {links.map((link) => (
          <ConfigureChannelButton key={link._id} link={link} onConfigure={onConfigure} />
        ))}
      </div>
      {isCollapsible ? (
        <button
          type="button"
          onClick={handleToggle}
          className="text-text-sub hover:text-text-strong flex items-center gap-0.5 self-start transition-colors"
        >
          <span className="text-label-xs font-medium">{isExpanded ? 'Show less' : `Show all (${links.length})`}</span>
          <RiArrowRightSLine className={cn('size-4 transition-transform', isExpanded && '-rotate-90')} />
        </button>
      ) : null}
    </div>
  );
}

function AddChannelButton({ onAddChannel }: { onAddChannel: () => void }) {
  return (
    <Button
      variant="secondary"
      mode="outline"
      size="xs"
      type="button"
      className="text-text-sub max-w-[275px] gap-1 px-1.5 py-1.5"
      onClick={onAddChannel}
      trailingIcon={RiArrowRightSLine}
    >
      Add channel
    </Button>
  );
}

type AddAnotherChannelStepProps = {
  agent: AgentResponse;
  links: AgentIntegrationLink[];
  planUsage: PlanUsage | undefined;
  readOnly: boolean;
  index: number;
  onAddChannel: () => void;
  onChannelAdded: (providerId: string, integration?: IIntegration) => void;
};

function AddAnotherChannelStep({
  agent,
  links,
  planUsage,
  readOnly,
  index,
  onAddChannel,
  onChannelAdded,
}: AddAnotherChannelStepProps) {
  return (
    <SetupStep
      index={index}
      status="current"
      indicator="dot"
      title="Add another channel"
      description="Add another channel provider for your users to message and interact with your agent."
      rightContent={
        readOnly ? (
          <AddChannelButton onAddChannel={onAddChannel} />
        ) : (
          <AddChannelPicker
            agentIdentifier={agent.identifier}
            agentName={agent.name}
            links={links}
            planUsage={planUsage}
            onSelected={onChannelAdded}
            renderTrigger={({ isBusy }) => (
              <Button
                variant="secondary"
                mode="outline"
                size="xs"
                type="button"
                disabled={isBusy}
                className="text-text-sub max-w-[275px] gap-1 px-1.5 py-1.5"
                trailingIcon={RiArrowRightSLine}
              >
                Add channel
              </Button>
            )}
          />
        )
      }
    />
  );
}

type AddAnotherChannelCardProps = {
  agent: AgentResponse;
  links: AgentIntegrationLink[];
  planUsage: PlanUsage | undefined;
  readOnly: boolean;
  persistKey: string;
  onAddChannel: () => void;
  onChannelAdded: (providerId: string, integration?: IIntegration) => void;
};

function AddAnotherChannelCard({
  agent,
  links,
  planUsage,
  readOnly,
  persistKey,
  onAddChannel,
  onChannelAdded,
}: AddAnotherChannelCardProps) {
  return (
    <SetupGuideCard label="What's next" persistKey={persistKey} className="min-w-0 flex-1">
      <SetupStepperRail className="gap-8 py-6 pb-3 pr-3 md:pr-6">
        <AddAnotherChannelStep
          agent={agent}
          links={links}
          planUsage={planUsage}
          readOnly={readOnly}
          index={1}
          onAddChannel={onAddChannel}
          onChannelAdded={onChannelAdded}
        />
      </SetupStepperRail>
    </SetupGuideCard>
  );
}

export function AgentWhatsNextSection({ agent }: AgentWhatsNextSectionProps) {
  const isWhatsNextEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_AGENT_WHATS_NEXT_ENABLED);
  const isMsTeamsWhatsNextEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_AGENT_MSTEAMS_WHATS_NEXT_ENABLED);
  const isEmailWhatsNextEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_AGENT_EMAIL_WHATS_NEXT_ENABLED);
  const { currentEnvironment, readOnly } = useEnvironment();
  const location = useLocation();
  const navigate = useNavigate();
  const agentRoutes = useAgentRoutes();
  const { isFreshSession } = useWhatsNextGuideSession(false);

  const integrationsQuery = useQuery({
    queryKey: getAgentIntegrationsQueryKey(currentEnvironment?._id, agent.identifier),
    queryFn: () =>
      listAgentIntegrations({
        environment: requireEnvironment(currentEnvironment, 'No environment selected'),
        agentIdentifier: agent.identifier,
        limit: 100,
      }),
    enabled: Boolean(isWhatsNextEnabled && currentEnvironment && agent.identifier),
  });

  const links = integrationsQuery.data?.data ?? [];
  const planUsage = integrationsQuery.data?.planUsage;
  const connectedLinks = useMemo(() => links.filter((link) => hasAgentInboundConnection(link.connectedAt)), [links]);
  const userRolloutLinks = useMemo(
    () =>
      filterUserRolloutLinks(connectedLinks, {
        isMsTeamsWhatsNextEnabled,
        isEmailWhatsNextEnabled,
      }),
    [connectedLinks, isMsTeamsWhatsNextEnabled, isEmailWhatsNextEnabled]
  );
  const { allRolledOut, isSettled: rolloutSettled } = useAgentChannelsRolloutStatus(connectedLinks);

  const integrationsTabPath = `${buildRoute(agentRoutes.detailsTab, {
    environmentSlug: currentEnvironment?.slug ?? '',
    agentIdentifier: encodeURIComponent(agent.identifier),
    agentTab: 'integrations',
  })}${location.search}`;

  const navigateToIntegrationDetail = useCallback(
    (integrationIdentifier: string) => {
      if (!currentEnvironment?.slug) {
        return;
      }

      navigate(
        `${buildRoute(agentRoutes.integrationDetail, {
          environmentSlug: currentEnvironment.slug,
          agentIdentifier: encodeURIComponent(agent.identifier),
          integrationIdentifier: encodeURIComponent(integrationIdentifier),
        })}${location.search}`
      );
    },
    [agent.identifier, agentRoutes.integrationDetail, currentEnvironment?.slug, location.search, navigate]
  );

  const handleConfigureChannel = useCallback(
    (link: AgentIntegrationLink) => {
      navigateToIntegrationDetail(link.integration.identifier);
    },
    [navigateToIntegrationDetail]
  );

  const handleAddChannel = useCallback(() => {
    navigate(integrationsTabPath);
  }, [integrationsTabPath, navigate]);

  const handleChannelAdded = useCallback(
    (_providerId: string, integration?: IIntegration) => {
      if (!integration?.identifier) {
        return;
      }

      navigateToIntegrationDetail(integration.identifier);
    },
    [navigateToIntegrationDetail]
  );

  if (!isWhatsNextEnabled) {
    return null;
  }

  if (integrationsQuery.isLoading || integrationsQuery.isError || connectedLinks.length === 0) {
    return null;
  }

  const persistKey = `agent-whats-next:${currentEnvironment?.slug ?? ''}:${agent.identifier}`;

  if (rolloutSettled && allRolledOut) {
    return (
      <AddAnotherChannelCard
        agent={agent}
        links={links}
        planUsage={planUsage}
        readOnly={readOnly}
        persistKey={persistKey}
        onAddChannel={handleAddChannel}
        onChannelAdded={handleChannelAdded}
      />
    );
  }

  if (!shouldShowAgentWhatsNextSection(connectedLinks, { isFreshSession })) {
    return null;
  }

  return (
    <SetupGuideCard label="What's next" persistKey={persistKey} className="min-w-0 flex-1">
      <SetupStepperRail className="gap-8 py-6 pb-3 pr-3 md:pr-6">
        {userRolloutLinks.length > 0 ? (
          <SetupStep
            index={1}
            status="current"
            indicator="dot"
            sectionLabel="FOR YOUR USERS"
            inlineSectionLabel
            title="Setup channels for your users"
            description="Setup the channels to let your users easily connect to this agent on wherever they are."
            rightContent={<ChannelList links={userRolloutLinks} onConfigure={handleConfigureChannel} />}
          />
        ) : null}
        <AddAnotherChannelStep
          agent={agent}
          links={links}
          planUsage={planUsage}
          readOnly={readOnly}
          index={userRolloutLinks.length > 0 ? 2 : 1}
          onAddChannel={handleAddChannel}
          onChannelAdded={handleChannelAdded}
        />
      </SetupStepperRail>
    </SetupGuideCard>
  );
}
